import React from 'react';

import PersonalisationSlotHeader from '../PersonalisationSlotHeader';
import { validateSlotSelection } from '../../utilities/stepValidation';
import { useAlphanumericSlotViewModel } from './useAlphanumericSlotViewModel';
import { getFontDisplayName } from '../../utilities/getFontDisplayName';

import inputStyles from './personalisation-inputs.module.css';
import gridStyles from './personalisation-grid.module.css';

// @TODO: This component needs tidying up - move all the logic to the useViewModel hook and move all the functions
// into useCallback hooks so we're not recreating them on every render.

/**
 * Alphanumeric input slot for personalisation.
 *
 * @param {Object} props - Component props.
 * @param {string} props.slot - Slot ID.
 * @param {Object} props.value - Slot configuration object.
 * @param {string|Object} [props.inputValue] - Current value of the input (string or {text,font,translatedText}).
 * @param {Function} props.onChange - Callback for value change.
 * @param {boolean} [props.suppressFontSelection] - When true, hides the font picker (used in GROUPED mode where font is shown at step level).
 * @param {boolean} [props.ariaInvalid] - When true, marks the input `aria-invalid="true"`. A parent sets it while
 *   showing its own error for this input; otherwise the attribute is not rendered.
 * @param {string} [props.describedBy] - An extra element id for the input's `aria-describedby` (e.g. a parent's
 *   error message), named after the restrictions helper rather than instead of it.
 * @param {boolean} [props.processHeading] - When false, the slot heading is shown exactly as configured, with no
 *   " (optional)" added for an optional slot.
 * @returns {import('react').JSX.Element}
 */
export default function PersonalisationAlphanumericSlot({
  value,
  inputValue,
  onChange,
  suppressFontSelection = false,
  ariaInvalid = false,
  describedBy = undefined,
  processHeading = true
}) {
  // Use the dedicated alphanumeric slot view model.
  const {
    id,
    inputLabel,
    isRequired,
    placeholderText = 'Enter text',
    maxLength,
    parsedAllowedFonts,
    selectedFont,
    hasFontOptions,
    inputRestrictionMessage,
    hasRestrictions,
    hasTranslation,
    isTranslating,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    handleFontChange,
    formatPrice,
    isDisabled,
    allowLetters,
    allowNumbers
  } = useAlphanumericSlotViewModel(value, onChange, inputValue);

  // A numbers-only slot (e.g. a year) gets the numeric keypad on mobile. Every other slot passes
  // `undefined`, so React renders no `inputmode` attribute and the input is exactly as before.
  const inputMode = allowNumbers && !allowLetters ? 'numeric' : undefined;

  // The restrictions helper first, then any id the parent adds. With neither, `undefined`, so no
  // `aria-describedby` is rendered - an input whose parent passes neither prop is unchanged.
  const ariaDescribedBy =
    [hasRestrictions ? `input-helper-text-${id}` : '', describedBy || ''].filter(Boolean).join(' ') || undefined;

  // Extract text from inputValue object or use empty string if it's a string.
  const textValue = (() => {
    if (inputValue && typeof inputValue === 'object' && inputValue !== null && 'text' in inputValue) {
      return inputValue['text'] || '';
    }
    return String(inputValue || '');
  })();

  // Extract translated text from inputValue object or use empty string if not available.
  const translatedTextValue = (() => {
    if (inputValue && typeof inputValue === 'object' && inputValue !== null && 'translatedText' in inputValue) {
      return inputValue['translatedText'] || '';
    }
    return '';
  })();

  // Check if the input is valid for validation styling.
  const isInputValid = validateSlotSelection({ slot: value, selectedValue: textValue });

  const renderFontSelection = () => {
    if (!Array.isArray(parsedAllowedFonts) || parsedAllowedFonts.length === 0) {
      return null;
    }

    return (
      <fieldset>
        {parsedAllowedFonts.length > 1 && (
          <legend
            className={
              inputStyles['personalisation__input-font-selection-label'] + ' body-xs body-uppercase font-medium'
            }
          >
            Choose font
          </legend>
        )}
        <ul
          className={
            gridStyles['personalisation__selection-grid'] +
            ' ' +
            gridStyles[`personalisation__selection-grid--3-column`]
          }
          role="list"
        >
          {parsedAllowedFonts.map((fontName, index) => (
            <li key={index} className={inputStyles['personalisation__input-font-selection-card']}>
              <input
                disabled={isDisabled}
                type="radio"
                id={`${value.id}-font-${index}`}
                name={`font-selection-${value.id}`}
                value={fontName}
                checked={selectedFont === fontName}
                onChange={() => handleFontChange(fontName)}
                className={inputStyles['personalisation__input-font-selection-hidden-input']}
              />
              <label
                htmlFor={`${value.id}-font-${index}`}
                className={inputStyles['personalisation__input-font-selection-option-label']}
              >
                <p
                  className={
                    inputStyles['personalisation__input-font-selection-font-preview'] +
                    ' ' +
                    inputStyles[
                      `personalisation__input-font-selection-font-preview--${fontName.replace(/\s+/g, '-')}`
                    ] +
                    ' body-lg'
                  }
                >
                  {textValue}
                </p>
                <p className={inputStyles['personalisation__input-font-selection-font-name'] + ' body-xs'}>
                  {getFontDisplayName({ fontName })}
                </p>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
    );
  };

  const renderTranslationField = () => {
    return (
      <div className={inputStyles['personalisation__translation-result']}>
        <label className="field__label" htmlFor={`translation__result__${id}`}>
          Inscription
        </label>
        <div className={inputStyles['personalisation__input-wrapper']}>
          <input
            className="field__input"
            id={`translation__result__${id}`}
            type="text"
            value={translatedTextValue}
            readOnly
            disabled
            placeholder={isTranslating ? `Translating...` : ''}
            aria-describedby={`translation-helper-text-${id}`}
          />
        </div>
        <p
          id={`translation-helper-text-${id}`}
          className={inputStyles['personalisation__input-helper-text'] + ' body-xs'}
        >
          This field shows the automatic translation of your input text.
        </p>
      </div>
    );
  };

  return (
    <>
      <PersonalisationSlotHeader slot={value} processHeading={processHeading} />
      <div
        className={
          inputStyles['personalisation__field'] +
          (isDisabled ? ' ' + inputStyles['personalisation__field--disabled'] : '')
        }
      >
        {inputLabel && (
          <label
            className={'field__label' + (isRequired ? ' field__label--required' : '')}
            htmlFor={`input__text__${id}`}
          >
            {inputLabel}
          </label>
        )}
        <div
          className={`${inputStyles['personalisation__input-wrapper']} ${isInputValid ? inputStyles['personalisation__input-wrapper--valid'] : ''}`}
        >
          <input
            className={`field__input ${isInputValid ? inputStyles['personalisation__input--valid'] : ''}`}
            id={`input__text__${id}`}
            type="text"
            inputMode={inputMode}
            aria-invalid={ariaInvalid ? 'true' : undefined}
            aria-describedby={ariaDescribedBy}
            placeholder={placeholderText}
            required={isRequired}
            maxLength={maxLength}
            value={textValue}
            disabled={isDisabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            onChange={(e) => handleInputChange(id, e.target.value)}
            onKeyDown={(e) => {
              if (handleKeyDown(e)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => handlePaste(e, id, textValue)}
          />
          <p className={inputStyles['personalisation__input-length'] + ' body-xs'}>
            {textValue.length}/{maxLength}
          </p>
        </div>
        {hasRestrictions && (
          <p id={`input-helper-text-${id}`} className={inputStyles['personalisation__input-helper-text'] + ' body-xs'}>
            {inputRestrictionMessage}
          </p>
        )}
        {value.overridePrice && !value.isFreeSlot && (
          <p
            className={
              inputStyles['personalisation__input-helper-text'] +
              ' ' +
              inputStyles['personalisation__input-override-price'] +
              ' body-xs'
            }
          >
            {value.feeDescription &&
              value.feeDescription !== '' &&
              value.feeDescription !== null &&
              value.feeDescription !== 'null' && <>{value.feeDescription} </>}
            +{formatPrice({ amount: value.overridePrice })}
          </p>
        )}
      </div>

      {hasTranslation && renderTranslationField()}

      {textValue && hasFontOptions && !suppressFontSelection && (
        <div className={inputStyles['personalisation__input-font-selection']}>{renderFontSelection()}</div>
      )}
    </>
  );
}
